import { ChevronRight } from '@mui/icons-material';
import type { RosterItemResponse } from '@on3/api';
import { Avatar } from '@on3/ui-lib/components/Avatar';
import { Link } from '@on3/ui-lib/components/Link';
import { StarRating } from '@on3/ui-lib/components/Player/StarRating';
import { StarRanking } from '@on3/ui-lib/components/Player/StarRating/Ranking';
import { isValidJerseyNumber } from '@on3/ui-lib/utils/checks';
import { classYearClaimCutoff } from '@on3/ui-lib/utils/constants';
import { formatCurrency } from '@on3/ui-lib/utils/formatters';
import { useDynamicRoute } from '@on3/ui-lib/utils/hooks/useDynamicRoute';
import { createUrl } from '@on3/ui-lib/utils/url';
import { VerifiedBadge } from '@public/components/Elite/VerifiedBadge/VerifiedBadge';
import { getAbbreviatedRank } from '@public/components/Nil/helpers';

import styles from './TeamRosterItem.module.scss';

export const TeamRosterItem = ({
  player,
  organization,
  rosterRating,
  rating,
  nilValue,
}: RosterItemResponse) => {
  const {
    defaultAsset,
    jerseyNumber,
    height,
    weight,
    hometown,
    highSchool,
    fullName,
    classYear,
    classRank,
    athleteVerified,
    slug,
    position,
    tier,
    age,
  } = player || {};

  const { formatHref } = useDynamicRoute();

  const showNil = !!nilValue?.totalValue && nilValue.totalValue >= 5000;

  const industryComparisonLink =
    slug &&
    formatHref({
      path: '/rivals/[id]/industry-comparison/',
      params: { id: slug },
      options: { resetQuery: true },
    });

  return (
    <div className={styles.playerWrapper}>
      {isValidJerseyNumber(jerseyNumber) && (
        <span aria-labelledby="jersey" className={styles.jersey} role="cell">
          {jerseyNumber}
        </span>
      )}
      <figure
        aria-labelledby="player-headshot"
        className={styles.avatar}
        role="cell"
      >
        <Avatar
          altText={`${fullName} Avatar`}
          domain={defaultAsset?.domain}
          fit="cover"
          height={100}
          quality={95}
          source={defaultAsset?.source}
          type={defaultAsset?.type}
          width-={100}
        />
      </figure>
      <figure
        aria-labelledby="class-rank"
        className={styles.classRank}
        role="cell"
      >
        <Avatar
          altText={`${organization?.fullName} logo`}
          domain={organization?.defaultAsset?.domain}
          height={20}
          source={organization?.defaultAsset?.source}
          teamAvatar
          title={organization?.fullName}
          type={organization?.defaultAsset?.type}
          width={20}
        />
        {classRank && <figcaption>{getAbbreviatedRank(classRank)}</figcaption>}
      </figure>

      <div className={styles.details}>
        <Link
          aria-labelledby="player"
          className={styles.name}
          href={`/rivals/${slug}/`}
          useSiteColor
        >
          {fullName}
        </Link>

        <div className={styles.vitalsContainer}>
          <dl className={styles.vitals}>
            <dt>Class year</dt>
            <dd>{classYear} H.S.</dd>

            {!!height && (
              <>
                <dt>Height</dt>
                <dd>{height}</dd>
              </>
            )}
            {!!weight && (
              <>
                <dt>Weight</dt>
                <dd>{weight}</dd>
              </>
            )}
          </dl>
          {age && (
            <dl className={styles.age} data-visible="mobile">
              <dt>Age</dt>
              <dd>{age} yrs</dd>
            </dl>
          )}
        </div>
        <div className={styles.homeContainer}>
          <dl>
            <dt>High School:</dt>
            <dd>
              <Link href={createUrl(['high-school', highSchool?.urlSlug])}>
                {highSchool?.name}
              </Link>
            </dd>
            {hometown?.abbr && (
              <>
                <dt>Hometown:</dt>
                <dd>({hometown.abbr})</dd>
              </>
            )}
          </dl>
        </div>
      </div>

      <div aria-labelledby="rating" className={styles.stars} role="cell">
        <div className={styles.rosterStars}>
          <StarRating
            fiveStarPlus={rosterRating?.fiveStarPlus}
            href={industryComparisonLink}
            rating={rosterRating?.rating}
            sport={rosterRating?.sport?.name}
            stars={rosterRating?.stars}
            year={String(rosterRating?.year)}
          />
          <Avatar
            altText={`${organization?.fullName} logo`}
            domain={organization?.defaultAsset?.domain}
            height={20}
            source={organization?.defaultAsset?.source}
            teamAvatar
            title={organization?.fullName}
            type={organization?.defaultAsset?.type}
            width={20}
          />
        </div>

        <StarRating
          className={styles.secondaryStars}
          fiveStarPlus={rating?.fiveStarPlus}
          href={industryComparisonLink}
          rating={rating?.consensusRating}
          sport={rating?.sport?.name}
          stars={rating?.consensusStars}
          year={String(rating?.year)}
        />

        <StarRanking
          isConsensus
          nationalRank={rating?.consensusNationalRank}
          positionAbbr={rating?.positionAbbr}
          positionRank={rating?.consensusPositionRank}
          sport={rating?.sport?.name}
          stateAbbr={rating?.stateAbbr}
          stateRank={rating?.consensusStateRank}
          year={rating?.year}
        />
      </div>

      <dl
        aria-labelledby="nil-value"
        className={styles.nilContainer}
        role="cell"
      >
        <dt>NIL Value:</dt>
        <dd>
          {showNil && nilValue.totalValue
            ? formatCurrency(nilValue.totalValue)
            : '-'}
        </dd>
      </dl>
      <div
        aria-labelledby="athlete-verified"
        className={styles.verified}
        role="cell"
      >
        {athleteVerified ? (
          <VerifiedBadge tier={tier} />
        ) : (classYear || 0) > classYearClaimCutoff ? (
          <Link
            forceReload
            href={createUrl(['rivals', slug, 'claim-profile'])}
            useSiteColor
          >
            Claim Profile <ChevronRight />
          </Link>
        ) : null}
      </div>

      <dl
        aria-labelledby="position"
        className={styles.positionContainer}
        role="cell"
      >
        <dt>Position Abbreviation</dt>
        <dd>{position?.abbr}</dd>
      </dl>
      {age && (
        <dl
          aria-labelledby="age"
          className={styles.age}
          data-visible="desktop"
          role="cell"
        >
          <dt>Age</dt>
          <dd>{age}</dd>
        </dl>
      )}
    </div>
  );
};
